import { Box, Grid, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { IConsultant, IGlobalJobDTO } from 'wisestep-api';
import {
  WsDetailsCard,
  WsButton,
  WsIconPersons,
  WsChip,
  WsChipVariants,
  ScrollableTabs,
  WsPagination,
  WsTypography,
  WsTypoVariants,
  Label,
  WsIconVisa,
  WsListingLoader,
  WsIconViewJD,
  WsDialog,
} from 'wisestep-base';

import JobPostersBulkEmail from '../../../../../components/BulkEmail/JobPostersBulkEmail';
import { openUrlInNewTab } from '../../../../../utils/openUrlInNewTab';
import ConsultantCard from '../HotJobs/ConsultantCard';

interface IMatchingJobsProps {
  selectedConsultant: IConsultant;
  matchedJobs: (IGlobalJobDTO & { isSelected: boolean })[];
  getMatchedJobs: (tabNumber: number) => void;
  pagesCount: number;
  loader: boolean;
  tabNumber: number;
  pageChange: (page: number) => void;
  page: number;
}

const MatchingJobs = ({
  selectedConsultant,
  matchedJobs,
  getMatchedJobs,
  pagesCount,
  loader,
  tabNumber,
  pageChange,
  page,
}: IMatchingJobsProps) => {
  const [selectedJobs, setSelectedJobs] = useState<IGlobalJobDTO[]>([]);
  const [openEmail, setOpenEmail] = useState(false);

  const [viewJd, setViewJd] = useState<{ view: boolean; jobIndex: number }>({
    view: false,
    jobIndex: 0,
  });

  const applyButton = (job, index) => (
    <Stack direction="row" gap={2} m={2} justifyContent="flex-end">
      <WsButton
        onClick={() => setViewJd({ view: true, jobIndex: index })}
        startIcon={<WsIconViewJD />}
        variant="outlined"
      >
        View JD
      </WsButton>
      <WsButton
        variant="contained"
        onClick={() => {
          setSelectedJobs([job]);
          {
            job.sourceOfJob
              ? ['email', 'email1'].includes(job.sourceOfJob.toLowerCase())
                ? setOpenEmail(true)
                : openUrlInNewTab(job.applyURL)
              : 'Apply on Web';
          }
        }}
      >
        {job.sourceOfJob
          ? ['email', 'email1'].includes(job.sourceOfJob.toLowerCase())
            ? 'Apply'
            : job.sourceOfJob
          : 'Apply on Web'}
      </WsButton>
    </Stack>
  );

  return (
    <>
      <Box p={3}>
        <ConsultantCard
          consultant={selectedConsultant}
          checkBoxOnclick={() => null}
          isActiveCard={false}
          gridXs={0}
          isCheckBox={false}
          isSelected={false}
        />
      </Box>
      <Stack direction="row" spacing={3}>
        <Box p={3}>
          <WsTypography variant={WsTypoVariants.SUB_TITLE}>
            Showing the best suitable jobs (posted by recruiters) for the candidate
          </WsTypography>
          <Label sx={{ fontWeight: 'bold' }}>Search Criteria</Label>
          <Label sx={{ fontWeight: 'bold' }}>{`Boolean: ${selectedConsultant.jobTitle}`}</Label>
          <Label sx={{ fontWeight: 'bold' }}>{`Posted In: 30 days `}</Label>
        </Box>
        <Box p={3}>
          <WsButton variant="outlined">Edit</WsButton>
        </Box>
      </Stack>
      {matchedJobs?.length > 0 && !loader ? (
        <>
          <ScrollableTabs
            scrollList={[
              { tabName: 'All Jobs' },
              { tabName: 'From Email' },
              { tabName: 'From Web' },
            ]}
            getTabIndex={(index: number) => getMatchedJobs(index)}
            defaultValue={tabNumber}
          />
          <Box p={2}>
            <WsPagination
              count={Math.ceil(pagesCount / 50)}
              onChange={(_, value) => {
                pageChange(value);
              }}
              itemsPerPage={50}
              defaultPage={(page ?? 0) + 1}
              pageNumber={page}
              isPageCount={true}
              type="Jobs"
              totalCount={pagesCount}
            />
          </Box>
          <Box pt={1} pr={1} display="flex" justifyContent="right">
            <WsButton
              variant="text"
              onClick={() => {
                setSelectedJobs(matchedJobs);
                setOpenEmail(true);
              }}
            >
              {tabNumber == 1 ? `Apply to all ${matchedJobs.length} jobs` : ''}
            </WsButton>
          </Box>
          <>
            {Array.isArray(matchedJobs) &&
              matchedJobs.map((job, index) => (
                <Grid container p={2} mt={1} key={job.id}>
                  <Grid item xs={12}>
                    <WsDetailsCard
                      icon={<WsIconPersons />}
                      title={job.title}
                      titleCode={job.company ?? 'Unknown'}
                      skillsArray={job?.skills ?? []}
                      detailsArray={[
                        {
                          icon: <WsIconVisa />,
                          labelName: job.visa ? job.visa : 'Not Available',
                        },
                      ]}
                      footer={applyButton(job, index)}
                      gridXs={8}
                      rightChildren={
                        <WsChip
                          value={
                            job.sourceOfJob
                              ? ['email', 'email1'].includes(job.sourceOfJob.toLowerCase())
                                ? 'EMAIL'
                                : job.sourceOfJob
                              : 'WEB'
                          }
                          variant={WsChipVariants.PRIMARY}
                        />
                      }
                    />
                  </Grid>
                </Grid>
              ))}
          </>
        </>
      ) : (
        <WsListingLoader />
      )}
      {openEmail && (
        <JobPostersBulkEmail
          toEmailList={selectedJobs
            .filter((job) => job.latestSenderEmail)
            .map((job) => ({
              email: job.latestSenderEmail ?? '',
              text: job.latestSenderEmail ?? '',
            }))}
          jobs={selectedJobs}
          consultants={[]}
          open={openEmail}
          handleClose={() => setOpenEmail(false)}
          template={''}
        />
      )}

      <WsDialog
        onClose={() => setViewJd({ view: false, jobIndex: 0 })}
        openDialog={viewJd.view}
        size="medium"
      >
        <Grid item container p={4} xs={12} justifyContent="space-between" alignItems="center">
          <WsTypography variant={WsTypoVariants.TITLE}>Job Description</WsTypography>
          <WsButton onClick={() => setViewJd({ view: false, jobIndex: 0 })}>Close</WsButton>
        </Grid>
        <Stack px={5} pt={2} pb={4}>
          {matchedJobs.length > 0 ? (
            <Typography
              dangerouslySetInnerHTML={{
                __html: matchedJobs[viewJd.jobIndex].description
                  ? matchedJobs[viewJd.jobIndex].description
                      ?.replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, 'illegalscript')
                      .replace(/\n/g, '<br>') ?? ''
                  : '',
              }}
            />
          ) : null}
        </Stack>
      </WsDialog>
    </>
  );
};

export default MatchingJobs;
